import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatIndianDate, formatIndianDateOnly, getStatusColorClass } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Phone, MessageSquare, Calendar, CheckCircle, XCircle, Clock, Check, ChevronsUpDown, Download } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPORT_ALLOWED_ROLES = ["Admin"];

const LEAD_SOURCES = [
  "Call", "Email", "WhatsApp", "Direct Visit", "India Mart", "Trade India", "By Reference", "Google", "Trade Shows", "Others"
];

const LEAD_STATUSES = [
  { value: "New", color: "bg-blue-500" },
  { value: "On Progress", color: "bg-yellow-500" },
  { value: "Success", color: "bg-green-500" },
  { value: "Failed", color: "bg-red-500" },
  { value: "Postponed", color: "bg-gray-500" }
];

const LeadsManager = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [followupDetailsDialogOpen, setFollowupDetailsDialogOpen] = useState(false);
  const [closeLeadDialogOpen, setCloseLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadFollowups, setLeadFollowups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [followupFilter, setFollowupFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 250;
  const [customerType, setCustomerType] = useState('Existing');
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  
  // Check if user can export
  const canExport = EXPORT_ALLOWED_ROLES.includes(user?.role);
  const protectedExport = useProtectedExport();
  
  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leads/export/csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };
  
  const [formData, setFormData] = useState({
    lead_source: '',
    customer_id: '',
    customer_type: 'Existing',
    inquiry_details: '',
    assigned_employee_id: '',
    due_date: '',
    lead_status: 'New',
    non_customer_name: '',
    non_customer_mobile: '',
    non_customer_email: '',
    interested_products: []
  });
  const [followupData, setFollowupData] = useState({
    followup_date: '',
    notes: '',
    next_due_date: '',
    status_update: ''
  });
  const [closureData, setClosureData] = useState({
    closure_status: '',
    closure_notes: ''
  });

  useEffect(() => {
    // Set default date range: 30 days before to 30 days after current date (using IST timezone)
    const getISTDate = () => {
      const now = new Date();
      // Convert to IST (UTC+5:30)
      const istOffset = 5.5 * 60 * 60 * 1000;
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const istTime = new Date(utcTime + istOffset);
      return istTime;
    };
    
    const today = getISTDate();
    const thirtyDaysBefore = new Date(today);
    thirtyDaysBefore.setDate(today.getDate() - 30);
    const thirtyDaysAfter = new Date(today);
    thirtyDaysAfter.setDate(today.getDate() + 30);
    
    setDateFrom(thirtyDaysBefore.toISOString().split('T')[0]);
    setDateTo(thirtyDaysAfter.toISOString().split('T')[0]);
    
    fetchLeads();
    fetchCustomers();
    fetchEmployees();
    fetchProducts();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leads/');
      // Sort by due_date (follow-up date) - earliest first, then by created_at for those without due_date
      const sortedLeads = response.data.sort((a, b) => {
        const dueDateA = a.next_followup_date || a.due_date;
        const dueDateB = b.next_followup_date || b.due_date;
        
        // If both have due dates, sort by due date (earliest first)
        if (dueDateA && dueDateB) {
          return new Date(dueDateA) - new Date(dueDateB);
        }
        // Leads with due dates come first
        if (dueDateA && !dueDateB) return -1;
        if (!dueDateA && dueDateB) return 1;
        // Fall back to created_at descending
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setLeads(sortedLeads);
    } catch (error) {
      toast.error('Failed to fetch leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadFollowups = async (leadId) => {
    setLoading(true);
    try {
      const response = await api.get(`/leads/${leadId}/followups`);
      setLeadFollowups(response.data);
    } catch (error) {
      toast.error('Failed to fetch followups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFollowups = async (lead) => {
    setSelectedLead(lead);
    await fetchLeadFollowups(lead.id);
    setFollowupDetailsDialogOpen(true);
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Use assignable/leads endpoint which allows Backoffice roles to fetch employees
      const response = await api.get('/employees/assignable/leads');
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      // Fallback to regular employees endpoint for Admin - include ALL active employees
      try {
        const fallbackResponse = await api.get('/employees/');
        const filtered = fallbackResponse.data.filter(e => e.is_active !== false);
        setEmployees(filtered);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  const handleOpenDialog = (lead = null) => {
    if (lead) {
      setSelectedLead(lead);
      setFormData({
        lead_source: lead.lead_source,
        customer_id: lead.customer_id || '',
        customer_type: lead.customer_type,
        inquiry_details: lead.inquiry_details,
        assigned_employee_id: lead.assigned_employee_id,
        due_date: lead.due_date,
        lead_status: lead.lead_status,
        non_customer_name: lead.non_customer_name || '',
        non_customer_mobile: lead.non_customer_mobile || '',
        non_customer_email: lead.non_customer_email || '',
        interested_products: lead.interested_products || []
      });
      setCustomerType(lead.customer_type);
    } else {
      setSelectedLead(null);
      setFormData({
        lead_source: '',
        customer_id: '',
        customer_type: 'Existing',
        inquiry_details: '',
        assigned_employee_id: '',
        due_date: '',
        lead_status: 'New',
        non_customer_name: '',
        non_customer_mobile: '',
        non_customer_email: '',
        interested_products: []
      });
      setCustomerType('Existing');
    }
    setCustomerSearchTerm('');
    setProductSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLead(null);
    setCustomerType('Existing');
    setProductSearchTerm('');
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const handleAddProduct = (product) => {
    if (!formData.interested_products.find(p => p.id === product.id)) {
      setFormData(prev => ({
        ...prev,
        interested_products: [...prev.interested_products, { id: product.id, name: product.name, sku: product.sku }]
      }));
    }
    setProductSearchOpen(false);
    setProductSearchTerm('');
  };

  const handleRemoveProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      interested_products: prev.interested_products.filter(p => p.id !== productId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedLead) {
        await api.put(`/leads/${selectedLead.id}`, formData);
        toast.success('Lead updated successfully');
      } else {
        await api.post('/leads/', formData);
        toast.success('Lead created successfully');
      }
      handleCloseDialog();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    setLoading(true);
    try {
      await api.post(`/leads/${selectedLead.id}/followups`, {
        lead_id: selectedLead.id,
        ...followupData
      });
      toast.success('Followup added successfully');
      setFollowupDialogOpen(false);
      setFollowupData({
        followup_date: '',
        notes: '',
        next_due_date: '',
        status_update: ''
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add followup');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseLead = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    setLoading(true);
    try {
      await api.put(`/leads/${selectedLead.id}`, closureData);
      toast.success('Lead closed successfully');
      setCloseLeadDialogOpen(false);
      setClosureData({
        closure_status: '',
        closure_notes: ''
      });
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close lead');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if followup is today or overdue
  const isFollowupToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followupDate = new Date(dateStr);
    followupDate.setHours(0, 0, 0, 0);
    return followupDate.getTime() === today.getTime();
  };

  const isFollowupOverdue = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followupDate = new Date(dateStr);
    followupDate.setHours(0, 0, 0, 0);
    return followupDate < today;
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.lead_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.inquiry_details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.non_customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.lead_status === statusFilter;
    
    // Assigned To filtering
    const matchesAssignedTo = assignedToFilter === 'all' || lead.assigned_employee_id === assignedToFilter;
    
    // Lead Source filtering
    const matchesLeadSource = leadSourceFilter === 'all' || lead.lead_source === leadSourceFilter;
    
    // Phone filtering
    const matchesPhone = !phoneFilter ||
      (lead.customer?.mobile || '').includes(phoneFilter) ||
      (lead.non_customer_mobile || '').includes(phoneFilter) ||
      (lead.contact_mobile || '').includes(phoneFilter);
    
    // Product filtering
    const matchesProduct = productFilter === 'all' || 
      (lead.interested_products && lead.interested_products.some(p => p.id === productFilter));
    
    // Followup date filtering
    let matchesFollowup = true;
    if (followupFilter === 'today') {
      matchesFollowup = isFollowupToday(lead.next_followup_date);
    } else if (followupFilter === 'overdue') {
      matchesFollowup = isFollowupOverdue(lead.next_followup_date) && 
        lead.lead_status !== 'Success' && lead.lead_status !== 'Failed';
    } else if (followupFilter === 'pending') {
      matchesFollowup = (isFollowupToday(lead.next_followup_date) || isFollowupOverdue(lead.next_followup_date)) &&
        lead.lead_status !== 'Success' && lead.lead_status !== 'Failed';
    }
    
    // Date filtering
    let matchesDateRange = true;
    if (dateFrom && dateTo) {
      const leadDate = new Date(lead.created_at);
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date
      matchesDateRange = leadDate >= fromDate && leadDate <= toDate;
    }
    
    return matchesSearch && matchesStatus && matchesAssignedTo && matchesLeadSource && matchesPhone && matchesProduct && matchesFollowup && matchesDateRange;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredLeads.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstRecord, indexOfLastRecord);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, phoneFilter, statusFilter, productFilter, followupFilter, assignedToFilter, leadSourceFilter, dateFrom, dateTo]);

  // Get unique products from all leads for filter dropdown
  const productsInLeads = [...new Set(
    leads.flatMap(lead => lead.interested_products || []).map(p => JSON.stringify({ id: p.id, name: p.name }))
  )].map(p => JSON.parse(p));

  // Count leads with pending followups
  const pendingFollowupsCount = leads.filter(lead => 
    (isFollowupToday(lead.next_followup_date) || isFollowupOverdue(lead.next_followup_date)) &&
    lead.lead_status !== 'Success' && lead.lead_status !== 'Failed'
  ).length;

  const getStatusBadgeVariant = (status) => {
    switch(status) {
      case 'New': return 'default';
      case 'On Progress': return 'secondary';
      case 'Success': return 'default';
      case 'Failed': return 'destructive';
      case 'Postponed': return 'outline';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Success': return <CheckCircle size={16} className="mr-1" />;
      case 'Failed': return <XCircle size={16} className="mr-1" />;
      case 'On Progress': return <Clock size={16} className="mr-1" />;
      default: return null;
    }
  };

  const handleOpenFollowup = (lead) => {
    setSelectedLead(lead);
    setFollowupDialogOpen(true);
  };

  // Render Lead Card for mobile view
  const renderLeadCard = (lead) => (
    <Card key={lead.id} className="mb-4">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-semibold text-blue-600">{lead.lead_no}</span>
            <Badge variant={getStatusBadgeVariant(lead.lead_status)} className="ml-2">
              {getStatusIcon(lead.lead_status)}
              {lead.lead_status}
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(lead)}>
              <Edit size={16} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleOpenFollowup(lead)}>
              <MessageSquare size={16} />
            </Button>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Customer:</span>
            <span className="font-medium">
              {lead.customer_type === 'Existing' 
                ? lead.customer?.company_name 
                : lead.non_customer_name}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Contact:</span>
            <span>{lead.contact_person || '-'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Product:</span>
            <span>{lead.product?.name || lead.other_product || '-'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Value:</span>
            <span className="font-medium">₹{(lead.expected_value || 0).toLocaleString('en-IN')}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Assigned:</span>
            <span>{lead.assigned_to_name || '-'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Created:</span>
            <span>{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN') : '-'}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Next Followup:</span>
            {lead.next_followup_date ? (
              <span className={`font-medium ${
                isFollowupOverdue(lead.next_followup_date) && lead.lead_status !== 'Success' && lead.lead_status !== 'Failed'
                  ? 'text-red-600 bg-red-100 px-2 py-0.5 rounded' 
                  : isFollowupToday(lead.next_followup_date) 
                    ? 'text-orange-600 bg-orange-100 px-2 py-0.5 rounded animate-pulse' 
                    : ''
              }`}>
                {new Date(lead.next_followup_date).toLocaleDateString('en-IN')}
                {isFollowupToday(lead.next_followup_date) && ' (Today)'}
                {isFollowupOverdue(lead.next_followup_date) && lead.lead_status !== 'Success' && lead.lead_status !== 'Failed' && ' (Overdue)'}
              </span>
            ) : '-'}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Leads & Followups</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage customer inquiries and track followups</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {canExport && (
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)} data-testid="export-csv-button" className="flex-1 sm:flex-none">
              <Download size={20} className="mr-2" />
              Export CSV
            </Button>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-lead-button" className="flex-1 sm:flex-none">
            <Plus size={20} className="mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Leads ({filteredLeads.length})</CardTitle>
              <CardDescription>View and manage all leads</CardDescription>
            </div>
            {pendingFollowupsCount > 0 && (
              <Badge variant="destructive" className="animate-pulse">
                {pendingFollowupsCount} Pending Followups
              </Badge>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3 mt-4 flex-wrap">
            <div className="flex-1 min-w-[150px] relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-32">
              <Input
                placeholder="Phone..."
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-32">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {LEAD_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Assigned To Filter */}
            <div className="w-full sm:w-36">
              <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assigned</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Lead Source Filter */}
            <div className="w-full sm:w-32">
              <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {LEAD_SOURCES.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-36">
              <Select value={followupFilter} onValueChange={setFollowupFilter}>
                <SelectTrigger className={followupFilter !== 'all' ? 'border-orange-500' : ''}>
                  <SelectValue placeholder="Followup" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Followups</SelectItem>
                  <SelectItem value="today">Due Today</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="pending">Pending (Today + Overdue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {productsInLeads.length > 0 && (
              <div className="w-full sm:w-36">
                <Select value={productFilter} onValueChange={setProductFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {productsInLeads.map(product => (
                      <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
                className="w-full sm:w-36"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
                className="w-full sm:w-36"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                const today = new Date();
                const ninetyDaysAgo = new Date(today);
                ninetyDaysAgo.setDate(today.getDate() - 90);
                setDateFrom(ninetyDaysAgo.toISOString().split('T')[0]);
                setDateTo(today.toISOString().split('T')[0]);
                setProductFilter('all');
                setAssignedToFilter('all');
                setLeadSourceFilter('all');
              }}
              className="w-full sm:w-auto"
            >
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !leads.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Phone size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No leads found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden space-y-4">
                {currentLeads.map((lead) => renderLeadCard(lead))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead No.</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Created Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Followups</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentLeads.map((lead) => {
                      // Check if lead needs highlighting (follow-up today or overdue, not closed)
                      const needsFollowupHighlight = (isFollowupToday(lead.next_followup_date) || 
                        (isFollowupOverdue(lead.next_followup_date) && 
                         lead.lead_status !== 'Success' && 
                         lead.lead_status !== 'Failed' &&
                         lead.closure_status !== 'Success' &&
                         lead.closure_status !== 'Failed'));
                      
                      // Determine row background color based on status (check both lead_status and closure_status)
                      const isSuccess = lead.lead_status === 'Success' || lead.closure_status === 'Success';
                      const rowBgClass = isSuccess 
                        ? 'bg-green-100' 
                        : needsFollowupHighlight 
                          ? 'bg-yellow-100' 
                          : '';
                      
                      return (
                      <TableRow key={lead.id} className={rowBgClass}>
                        <TableCell className="font-medium">{lead.lead_no}</TableCell>
                        <TableCell>
                          {lead.customer_type === 'Non-Customer' ? (
                            <div>
                              <span className="font-medium">{lead.non_customer_name}</span>
                              <br />
                              <span className="text-xs text-gray-500">{lead.non_customer_mobile}</span>
                              {lead.non_customer_email && (
                                <>
                                  <br />
                                  <span className="text-xs text-gray-500">{lead.non_customer_email}</span>
                                </>
                              )}
                            </div>
                          ) : (
                            lead.customer?.company_name || 'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{lead.lead_source}</Badge>
                        </TableCell>
                        <TableCell>
                          {lead.interested_products && lead.interested_products.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {lead.interested_products.slice(0, 2).map((p, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{p.name}</Badge>
                              ))}
                              {lead.interested_products.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{lead.interested_products.length - 2}</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>{lead.assigned_employee?.name || 'Unassigned'}</TableCell>
                        <TableCell>
                          <span className="text-gray-600">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN') : '-'}
                          </span>
                        </TableCell>
                        <TableCell>{formatIndianDateOnly(lead.due_date)}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={getStatusBadgeVariant(lead.lead_status)} 
                            className={`flex items-center w-fit ${getStatusColorClass(lead.lead_status)}`}
                          >
                            {getStatusIcon(lead.lead_status)}
                            {lead.lead_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-normal text-blue-600 hover:text-blue-800"
                            onClick={() => handleViewFollowups(lead)}
                          >
                            {lead.followup_count || 0} followup{lead.followup_count !== 1 ? 's' : ''}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                              setSelectedLead(lead);
                              setFollowupDialogOpen(true);
                            }}
                            title="Add Followup"
                          >
                            <MessageSquare size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(lead)}
                            title="Edit Lead"
                          >
                            <Edit size={16} />
                          </Button>
                          {!lead.closure_status && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLead(lead);
                                setCloseLeadDialogOpen(true);
                              }}
                              title="Close Lead"
                            >
                              <CheckCircle size={16} className="text-green-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              </div>

              {/* Pagination Controls */}
              {filteredLeads.length > 0 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <div className="text-sm text-gray-600">
                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredLeads.length)} of {filteredLeads.length} leads
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-3">
                      Page {currentPage} of {totalPages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Lead Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
            <DialogDescription>
              {selectedLead ? 'Update lead information' : 'Create a new lead inquiry'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lead_source">Lead Source *</Label>
                <Select
                  value={formData.lead_source}
                  onValueChange={(value) => setFormData({ ...formData, lead_source: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(source => (
                      <SelectItem key={source} value={source}>{source}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {!selectedLead && (
                <div className="space-y-2">
                  <Label htmlFor="customer_type">Customer Type *</Label>
                  <Select
                    value={customerType}
                    onValueChange={(value) => {
                      setCustomerType(value);
                      setFormData({ ...formData, customer_type: value, customer_id: '', non_customer_name: '', non_customer_mobile: '', non_customer_email: '' });
                    }}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Existing">Existing Customer</SelectItem>
                      <SelectItem value="Non-Customer">Non-Customer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {customerType === 'Existing' && (
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="customer_id">Search Customer *</Label>
                  <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={customerSearchOpen}
                        className="w-full justify-between"
                      >
                        {formData.customer_id
                          ? customers.find((customer) => customer.id === formData.customer_id)?.company_name
                          : "Search and select customer..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[600px] p-0">
                      <Command shouldFilter={false}>
                        <CommandInput 
                          placeholder="Search customer by name, mobile, or email..." 
                          value={customerSearchTerm}
                          onValueChange={setCustomerSearchTerm}
                        />
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {customers
                            .filter(c => {
                              const searchLower = customerSearchTerm.toLowerCase();
                              return c.company_name.toLowerCase().includes(searchLower) ||
                                     c.contact_person.toLowerCase().includes(searchLower) ||
                                     c.mobile.includes(customerSearchTerm) ||
                                     c.email.toLowerCase().includes(searchLower);
                            })
                            .map((customer) => (
                              <CommandItem
                                key={customer.id}
                                value={customer.id}
                                onSelect={() => {
                                  setFormData({ ...formData, customer_id: customer.id });
                                  setCustomerSearchOpen(false);
                                  setCustomerSearchTerm('');
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{customer.company_name}</span>
                                  <span className="text-sm text-gray-500">
                                    {customer.contact_person} • {customer.mobile}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {customerType === 'Non-Customer' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="non_customer_name">Contact Name *</Label>
                    <Input
                      id="non_customer_name"
                      value={formData.non_customer_name}
                      onChange={(e) => setFormData({ ...formData, non_customer_name: e.target.value })}
                      required
                      placeholder="Enter contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="non_customer_mobile">Mobile Number *</Label>
                    <Input
                      id="non_customer_mobile"
                      value={formData.non_customer_mobile}
                      onChange={(e) => setFormData({ ...formData, non_customer_mobile: e.target.value })}
                      required
                      placeholder="Enter mobile number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="non_customer_email">Email (Optional)</Label>
                    <Input
                      id="non_customer_email"
                      type="email"
                      value={formData.non_customer_email}
                      onChange={(e) => setFormData({ ...formData, non_customer_email: e.target.value })}
                      placeholder="Enter email address"
                      data-testid="lead-non-customer-email"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2 col-span-2">
                <Label htmlFor="inquiry_details">Inquiry / Requirement Details *</Label>
                <Textarea
                  id="inquiry_details"
                  value={formData.inquiry_details}
                  onChange={(e) => setFormData({ ...formData, inquiry_details: e.target.value })}
                  required
                  rows={4}
                  placeholder="Describe the customer's inquiry or requirements..."
                />
              </div>

              {/* Interested Products - Optional */}
              <div className="space-y-2 col-span-2">
                <Label>Interested Products (Optional)</Label>
                <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-left">
                      <span className="text-gray-500">Search and add products...</span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Search by name or SKU..." 
                        value={productSearchTerm}
                        onValueChange={setProductSearchTerm}
                      />
                      <CommandEmpty>No products found</CommandEmpty>
                      <CommandGroup className="max-h-48 overflow-y-auto">
                        {filteredProducts.slice(0, 20).map(product => (
                          <CommandItem 
                            key={product.id} 
                            value={product.id}
                            onSelect={() => handleAddProduct(product)}
                          >
                            <Check className={`mr-2 h-4 w-4 ${formData.interested_products.find(p => p.id === product.id) ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="flex-1">
                              <div className="font-medium">{product.name}</div>
                              <div className="text-xs text-gray-500">SKU: {product.sku}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
                {formData.interested_products.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.interested_products.map(product => (
                      <Badge 
                        key={product.id} 
                        variant="secondary" 
                        className="cursor-pointer hover:bg-red-100 transition-colors"
                        onClick={() => handleRemoveProduct(product.id)}
                      >
                        {product.name}
                        <XCircle className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="assigned_employee_id">Assigned Employee *</Label>
                <Select
                  value={formData.assigned_employee_id}
                  onValueChange={(value) => setFormData({ ...formData, assigned_employee_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(employee => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name} - {employee.role}{employee.mobile ? ` (${employee.mobile})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              {selectedLead && (
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="lead_status">Lead Status *</Label>
                  <Select
                    value={formData.lead_status}
                    onValueChange={(value) => setFormData({ ...formData, lead_status: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : selectedLead ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Followup Dialog */}
      <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Followup</DialogTitle>
            <DialogDescription>
              Add a followup note for lead {selectedLead?.lead_no}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddFollowup}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="followup_date">Followup Date *</Label>
                <Input
                  id="followup_date"
                  type="date"
                  value={followupData.followup_date}
                  onChange={(e) => setFollowupData({ ...followupData, followup_date: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Followup Notes *</Label>
                <Textarea
                  id="notes"
                  value={followupData.notes}
                  onChange={(e) => setFollowupData({ ...followupData, notes: e.target.value })}
                  required
                  rows={4}
                  placeholder="Enter followup details, discussion points, next steps..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status_update">Update Lead Status (Optional)</Label>
                <Select
                  value={followupData.status_update || 'no-change'}
                  onValueChange={(value) => setFollowupData({ ...followupData, status_update: value === 'no-change' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep current status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-change">No Change</SelectItem>
                    {LEAD_STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="next_due_date">Next Followup Date (Optional)</Label>
                <Input
                  id="next_due_date"
                  type="date"
                  value={followupData.next_due_date}
                  onChange={(e) => setFollowupData({ ...followupData, next_due_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFollowupDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Followup'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Followup Details Dialog */}
      <Dialog open={followupDetailsDialogOpen} onOpenChange={setFollowupDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Followup History - {selectedLead?.lead_no}</DialogTitle>
            <DialogDescription>
              All followups and closure details for this lead
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {leadFollowups.length === 0 && !selectedLead?.closure_status ? (
              <p className="text-center text-gray-500 py-8">No followups recorded yet</p>
            ) : (
              <div className="space-y-4">
                {/* Closure Information */}
                {selectedLead?.closure_status && (
                  <Card className="border-2 border-green-200 bg-green-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="text-green-600" size={20} />
                        Lead Closed
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-gray-600">Final Status</Label>
                          <p className="font-medium mt-1">
                            <Badge variant={selectedLead.closure_status === 'Success' ? 'default' : 'destructive'}>
                              {selectedLead.closure_status}
                            </Badge>
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-600">Closed On</Label>
                          <p className="font-medium mt-1">{formatIndianDate(selectedLead.updated_at)}</p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Closure Notes</Label>
                        <p className="mt-1 text-sm bg-white p-3 rounded border">{selectedLead.closure_notes}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Followups List */}
                {leadFollowups.map((followup, index) => (
                  <Card key={followup.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <Badge variant="outline" className="mb-2">
                            Followup #{leadFollowups.length - index}
                          </Badge>
                          <p className="text-sm text-gray-600">
                            by <span className="font-medium">{followup.creator?.name || 'Unknown'}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {formatIndianDateOnly(followup.followup_date)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatIndianDate(followup.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium text-gray-600">Notes</Label>
                          <p className="mt-1 text-sm bg-gray-50 p-3 rounded">{followup.notes}</p>
                        </div>
                        
                        {followup.status_update && (
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium text-gray-600">Status Updated:</Label>
                            <Badge variant="secondary" className={getStatusColorClass(followup.status_update)}>
                              {followup.status_update}
                            </Badge>
                          </div>
                        )}
                        
                        {followup.next_due_date && (
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-500" />
                            <Label className="text-sm font-medium text-gray-600">Next Followup:</Label>
                            <span className="text-sm">{formatIndianDateOnly(followup.next_due_date)}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setFollowupDetailsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Lead Dialog */}
      <Dialog open={closeLeadDialogOpen} onOpenChange={setCloseLeadDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Close Lead</DialogTitle>
            <DialogDescription>
              Mark lead {selectedLead?.lead_no} as closed with final status
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCloseLead}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="closure_status">Final Status *</Label>
                <Select
                  value={closureData.closure_status}
                  onValueChange={(value) => setClosureData({ ...closureData, closure_status: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select final status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Success">Success</SelectItem>
                    <SelectItem value="Failed">Failed</SelectItem>
                    <SelectItem value="Postponed">Postponed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="closure_notes">Closure Notes *</Label>
                <Textarea
                  id="closure_notes"
                  value={closureData.closure_notes}
                  onChange={(e) => setClosureData({ ...closureData, closure_notes: e.target.value })}
                  required
                  rows={4}
                  placeholder="Provide detailed closure notes, outcome, and any relevant information..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloseLeadDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Closing...' : 'Close Lead'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default LeadsManager;
